// filepath: c:\git_mmpc\reservation-web\src\components\BirthdateInput.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  TextField,
  Typography,
  Box,
  FormHelperText,
  InputAdornment
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';

const BirthdateInput = ({ 
  onBirthdateChange, 
  label = "Date of Birth", 
  required = false,
  minAge = 18,
  initialValue = null,
  value = null,
  disabled = false,
  error = null
}) => {
  const [birthdate, setBirthdate] = useState(value ? formatDateForInput(value) : (initialValue ? formatDateForInput(initialValue) : ''));
  const [internalError, setInternalError] = useState('');
  const [age, setAge] = useState(null);
  const [isAndroid, setIsAndroid] = useState(false);

  // Detect Android platform
  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    setIsAndroid(/android/.test(userAgent));
  }, []);

  // Update birthdate when value prop changes
  useEffect(() => {
    if (value) {
      setBirthdate(formatDateForInput(value));
    }
  }, [value]);

  // Format date from Date object to YYYY-MM-DD for input
  function formatDateForInput(date) {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  }

  // Parse date from YYYY-MM-DD to Date object
  function parseInputDate(dateString) {
    if (!dateString) return null;
    return new Date(dateString);
  }

  const calculateAge = (birthDate) => {
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const validateBirthdate = useCallback((dateString) => {
    if (!dateString) {
      if (required) {
        setInternalError('Date of birth is required');
        setAge(null);
        return false;
      }
      setInternalError('');
      setAge(null);
      return true;
    }

    const date = parseInputDate(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      setInternalError('Please enter a valid date');
      setAge(null);
      return false;
    }

    // Check if date is in the future
    const today = new Date();
    if (date > today) {
      setInternalError('Date of birth cannot be in the future');
      setAge(null);
      return false;
    }

    // Check if user is at least minAge years old
    const calculatedAge = calculateAge(date);
    setAge(calculatedAge);
    
    if (calculatedAge < minAge) {
      setInternalError(`You must be at least ${minAge} years old`);
      return false;
    }

    setInternalError('');
    return true;
  }, [required, minAge]);

  const handleDateChange = (event) => {
    const dateString = event.target.value;
    setBirthdate(dateString);
    const isValid = validateBirthdate(dateString);
    
    if (onBirthdateChange) {
      onBirthdateChange(isValid ? parseInputDate(dateString) : null);
    }
  };

  // Validate on mount if initialValue is provided
  useEffect(() => {
    if (initialValue) {
      validateBirthdate(formatDateForInput(initialValue));
    }
  }, [initialValue, validateBirthdate]);

  return (
    <Box>
      <Typography 
        variant="subtitle1" 
        gutterBottom
        sx={{ 
          display: 'flex', 
          alignItems: 'center' 
        }}
      >
        {label} {required && <Typography color="error" component="span" ml={0.5}>*</Typography>}
      </Typography>
      
      <TextField
        type="date"
        fullWidth
        variant="outlined"
        value={birthdate}
        onChange={handleDateChange}
        error={!!error || !!internalError}
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EventIcon />
            </InputAdornment>
          ),
        }}
        inputProps={{
          max: new Date().toISOString().split('T')[0], // Prevent future dates
        }}
        sx={{
          // Android-specific styling to make date picker more accessible
          ...(isAndroid && {
            '& input::-webkit-calendar-picker-indicator': {
              width: '100%',
              height: '100%',
              position: 'absolute',
              opacity: 0,
              cursor: 'pointer',
            }
          })
        }}
      />
      
      {(error || internalError) && (
        <FormHelperText error>{error || internalError}</FormHelperText>
      )}
      
      {age !== null && !error && !internalError && (
        <FormHelperText>
          Age: {age} years
        </FormHelperText>
      )}
    </Box>
  );
};

export default BirthdateInput;
